import { ApolloClient } from '@apollo/client'
import { resolveValue } from 'components/form/core/hooks/useField'
import { TipoGravidezSelectFieldModel } from 'components/form/field/select/TipoGravidezSelectField'
import { Calculation } from 'final-form-calculate'
import { FonteIdadeGestacionalEnum, SituacaoProblema, TipoGravidezEnum } from 'graphql/types.generated'
import { isEqual } from 'lodash'
import { emptyArrayOf } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { isAfter, isAfterOrEqual, isBeforeOrEqual } from 'util/date/compare'
import { toDate } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'
import { CiapCidPreNatal, meta as metaSoapState, SoapState } from 'view/atendimentos/atendimento-individual/model'

import { IdadeGestacionalAcompanhamentoPreNatal } from '../aside/pre-natal/model-acompanhamentoPreNatal'
import { Problema } from '../aside/types/ProblemaModel'
import { AvaliacaoFormModel } from '../avaliacao/AvaliacaoForm'
import { ProblemaCondicaoModel } from '../avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import {
  addProblemaCondicaoAutomatico,
  createProblemaCondicaoAutomaticoPreNatal,
  findProblemaComCiapW78,
  removeProblemaCondicao,
} from '../avaliacao/components/problemas-condicoes/utils/operations-problemasCondicoes'
import {
  hasProblemaCondicaoComCiapW78,
  hasProblemaCondicaoDePreNatal,
} from '../avaliacao/components/problemas-condicoes/utils/verifications-preNatal'
import { FormAtivoObjetivoEnum } from '../objetivo/model'
import { ObjetivoFormModel } from '../objetivo/ObjetivoForm'
import {
  filterResultadosExamesAndConvertToExamePreNatalInput,
  isResultadoExamePreNatal,
} from '../objetivo/resultados-exames/util-resultadosExames'
import { getIdadeGestacional, getPreNatalCalculatorStatus } from './util-preNatal'

export const createPreNatalObjetivoCalculations = (
  objetivo: MetaPath<ObjetivoFormModel>,
  avaliacao: MetaPath<AvaliacaoFormModel>,
  isGestante: boolean,
  hasPermissionPreNatal: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean,
  isObservacaoAndPreNatal: boolean
): Calculation[] => [
  {
    field: avaliacao.problemasECondicoes.absolutePath(),
    updates: {
      [objetivo.atendimentosEspecificos.formAtivo.absolutePath()]: (
        problemasCondicoes: ProblemaCondicaoModel[],
        allValues: SoapState
      ) => {
        const formAtivo = resolveValue(allValues, objetivo.atendimentosEspecificos.formAtivo)
        if (hasPermissionPreNatal) {
          if (isGestante && hasProblemaCondicaoDePreNatal(problemasCondicoes, hasProblemaComCiapW78AtivoPersistido)) {
            return FormAtivoObjetivoEnum.PRE_NATAL
          } else if (formAtivo === FormAtivoObjetivoEnum.PRE_NATAL && !isObservacaoAndPreNatal) {
            return null
          } else {
            return formAtivo
          }
        } else {
          return formAtivo
        }
      },
    },
  },
]

export const createIdadeGestacionalCalculation = (
  metaIdadeGestacional: MetaPath<IdadeGestacionalAcompanhamentoPreNatal>,
  objetivo: MetaPath<ObjetivoFormModel>,
  avaliacao: MetaPath<AvaliacaoFormModel>,
  apollo: ApolloClient<object>,
  prontuarioId: ID,
  dataAtendimento: Instant,
  hasPermissionAcompanhamentoPreNatal: boolean,
  ultimaDumPreNatalAtivo: LocalDate,
  isGestante: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean
): Calculation => ({
  field: [
    objetivo.resultadosExames.absolutePath(),
    objetivo.dum.absolutePath(),
    metaSoapState.preNatal.fonteIdadeGestacional.absolutePath(),
    avaliacao.problemasECondicoes.absolutePath(),
  ],
  updates: {
    [metaIdadeGestacional.absolutePath()]: async (_, allValues: SoapState, prevValues: SoapState) => {
      const isAtendimentoPreNatal = hasProblemaCondicaoDePreNatal(
        allValues.avaliacao?.problemasECondicoes,
        hasProblemaComCiapW78AtivoPersistido
      )
      const dumAtendimentoAtual = allValues.objetivo?.dum
      const prevDumAtendimentoAtual = prevValues.objetivo?.dum

      const hasNoDum = !ultimaDumPreNatalAtivo && !dumAtendimentoAtual
      const skipCalculateIdadeGestacional = !(isGestante || isAtendimentoPreNatal) || hasNoDum

      if (!hasPermissionAcompanhamentoPreNatal || skipCalculateIdadeGestacional) return null

      const fonteIdadeGestacional = allValues.preNatal?.fonteIdadeGestacional
      const prevFonteIdadeGestacional = prevValues.preNatal?.fonteIdadeGestacional
      const hasFonteIdadeGestacionalChanged = !isEqual(fonteIdadeGestacional, prevFonteIdadeGestacional)

      const resultadosExames = allValues.objetivo?.resultadosExames
      const prevResultadosExames = prevValues.objetivo?.resultadosExames

      const resultadosExamesPreNatalAsInput =
        resultadosExames &&
        filterResultadosExamesAndConvertToExamePreNatalInput(resultadosExames, isResultadoExamePreNatal)
      const prevResultadosExamesPreNatalAsInput =
        prevResultadosExames &&
        filterResultadosExamesAndConvertToExamePreNatalInput(prevResultadosExames, isResultadoExamePreNatal)

      const hasResultadosExamesPreNatalChanged = !isEqual(
        resultadosExamesPreNatalAsInput,
        prevResultadosExamesPreNatalAsInput
      )

      const hasNewProblemaCondicaoPreNatal =
        isAtendimentoPreNatal &&
        !hasProblemaCondicaoDePreNatal(prevValues.avaliacao?.problemasECondicoes, hasProblemaComCiapW78AtivoPersistido)
      const hasDumAtendimentoAtualChanged = !isEqual(dumAtendimentoAtual, prevDumAtendimentoAtual)

      const mustCalculateIdadeGestacional =
        hasDumAtendimentoAtualChanged ||
        hasNewProblemaCondicaoPreNatal ||
        hasResultadosExamesPreNatalChanged ||
        hasFonteIdadeGestacionalChanged

      if (mustCalculateIdadeGestacional) {
        return await getIdadeGestacional(
          prontuarioId,
          dataAtendimento,
          apollo,
          resultadosExamesPreNatalAsInput,
          dumAtendimentoAtual ?? ultimaDumPreNatalAtivo,
          fonteIdadeGestacional
        )
      } else {
        return allValues.acompanhamentoPreNatal?.idadeGestacional
      }
    },
  },
})

export const createPreNatalAvaliacaoCalculations = (
  avaliacao: MetaPath<AvaliacaoFormModel>,
  dataNascimentoCidadao: LocalDate,
  dataAtendimento: Instant,
  ciapCidPreNatal: CiapCidPreNatal,
  isContinuacaoPreNatal: boolean,
  hasPermissionPreNatal: boolean,
  problemasCidadao?: Problema[]
): readonly Calculation[] =>
  hasPermissionPreNatal
    ? [
        createEncerrarGestacaoDataDesfechoCalculation(
          avaliacao,
          dataNascimentoCidadao,
          dataAtendimento,
          ciapCidPreNatal,
          problemasCidadao
        ),
        createPreNatalProblemasCondicoesAnterioresCalculation(
          avaliacao,
          dataNascimentoCidadao,
          dataAtendimento,
          ciapCidPreNatal,
          !isContinuacaoPreNatal,
          problemasCidadao
        ),
      ]
    : emptyArrayOf<Calculation>()

export const createPreNatalProblemasCondicoesAnterioresCalculation = (
  avaliacao: MetaPath<AvaliacaoFormModel>,
  dataNascimentoCidadao: LocalDate,
  dataAtendimento: Instant,
  ciapCidPreNatal: CiapCidPreNatal,
  isCidadaSemGestacaoAtiva: boolean,
  problemasCidadao?: Problema[]
): Calculation => ({
  field: avaliacao.problemasECondicoes.absolutePath(),
  updates: {
    [metaSoapState.problemasECondicoes.absolutePath()]: (
      problemasCondicoesAvaliados: ProblemaCondicaoModel[],
      allValues: SoapState,
      prevValues: SoapState
    ) => {
      let problemasCondicoesAnteriores =
        resolveValue<ProblemaCondicaoModel[]>(allValues, metaSoapState.problemasECondicoes) ??
        emptyArrayOf<ProblemaCondicaoModel>()

      const hasProblemaW78Avaliado = hasProblemaCondicaoComCiapW78(problemasCondicoesAvaliados)

      if (isCidadaSemGestacaoAtiva && !hasProblemaW78Avaliado) {
        const {
          mustRemoveW78NosProblemasCondicoesAnteriores,
          mustAddW78NosProblemasCondicoesAnteriores,
          dataInicioProblemaAltoRisco,
        } = getPreNatalCalculatorStatus(
          avaliacao,
          prevValues,
          problemasCondicoesAvaliados,
          problemasCondicoesAnteriores,
          problemasCidadao
        )

        if (mustRemoveW78NosProblemasCondicoesAnteriores) {
          problemasCondicoesAnteriores = removeProblemaCondicao(problemasCondicoesAnteriores, {
            ciapToRemove: ciapCidPreNatal.ciap?.codigo,
            cidToRemove: ciapCidPreNatal.cid?.codigo,
          })
        }
        if (mustAddW78NosProblemasCondicoesAnteriores) {
          return addProblemaCondicaoAutomatico(
            problemasCondicoesAnteriores,
            createProblemaCondicaoAutomaticoPreNatal(
              ciapCidPreNatal,
              SituacaoProblema.ATIVO,
              null,
              null,
              dataNascimentoCidadao,
              dataInicioProblemaAltoRisco,
              null,
              dataAtendimento
            )
          )
        }
      }

      return problemasCondicoesAnteriores
    },
  },
})

const createEncerrarGestacaoDataDesfechoCalculation = (
  avaliacao: MetaPath<AvaliacaoFormModel>,
  dataNascimentoCidadao: LocalDate,
  dataAtendimento: Instant,
  ciapCidPreNatal: CiapCidPreNatal,
  problemasCidadao?: Problema[]
): Calculation => ({
  field: avaliacao.encerrarGestacao.dataDesfecho.absolutePath(),
  updates: {
    [metaSoapState.problemasECondicoes.absolutePath()]: (
      dataDesfecho: LocalDate,
      allValues: SoapState,
      prevValues: SoapState
    ) => {
      let listaProblemasCondicoesAnteriores =
        resolveValue<ProblemaCondicaoModel[]>(allValues, metaSoapState.problemasECondicoes) ??
        emptyArrayOf<ProblemaCondicaoModel>()

      const dataDesfechoAnterior = resolveValue<string>(
        prevValues,
        metaSoapState.avaliacao.encerrarGestacao.dataDesfecho
      )

      const problemaPreNatalJaExistente = findProblemaComCiapW78(problemasCidadao)

      if (dataDesfecho !== dataDesfechoAnterior) {
        listaProblemasCondicoesAnteriores = handleApagarDesfecho(listaProblemasCondicoesAnteriores, ciapCidPreNatal)
      }
      if (
        dataDesfecho &&
        isDataDesfechoValid(
          dataDesfecho,
          dataNascimentoCidadao,
          dataAtendimento,
          problemaPreNatalJaExistente?.ultimaEvolucao?.dataInicio
        )
      ) {
        return handleAdicionarDesfecho(
          listaProblemasCondicoesAnteriores,
          dataNascimentoCidadao,
          dataDesfecho,
          ciapCidPreNatal,
          problemaPreNatalJaExistente
        )
      }
      return listaProblemasCondicoesAnteriores
    },
  },
})

const handleAdicionarDesfecho = (
  listaProblemasCondicoesAnteriores: readonly ProblemaCondicaoModel[],
  dataNascimentoCidadao: LocalDate,
  dataDesfecho: LocalDate,
  ciapCidPreNatal: CiapCidPreNatal,
  problemaPreNatalJaExistente: Problema
) =>
  addProblemaCondicaoAutomatico(
    listaProblemasCondicoesAnteriores,
    createProblemaCondicaoAutomaticoPreNatal(
      ciapCidPreNatal,
      SituacaoProblema.RESOLVIDO,
      problemaPreNatalJaExistente?.cid10,
      problemaPreNatalJaExistente,
      dataNascimentoCidadao,
      null,
      dataDesfecho
    )
  )

const handleApagarDesfecho = (
  listaProblemasCondicoesAnteriores: readonly ProblemaCondicaoModel[],
  ciapCidPreNatal: CiapCidPreNatal
) =>
  removeProblemaCondicao(listaProblemasCondicoesAnteriores, {
    ciapToRemove: ciapCidPreNatal.ciap?.codigo,
    situacaoToRemove: SituacaoProblema.RESOLVIDO,
    isAutomaticoToRemove: true,
  })

const isDataDesfechoValid = (
  dataDesfecho: string,
  dataNascimentoCidadao: string,
  dataAtendimento: Instant,
  dataInicioGestacao?: string
): boolean => {
  const dataDesfechoAsDate = toDate(dataDesfecho)
  return (
    isAfter(dataDesfecho, dataNascimentoCidadao) &&
    isBeforeOrEqual(dataDesfechoAsDate, dataAtendimento) &&
    ((dataInicioGestacao && isAfterOrEqual(dataDesfechoAsDate, toDate(dataInicioGestacao))) ?? true)
  )
}

export const preNatalCalculations: Calculation[] = [
  {
    field: metaSoapState.preNatal.tipoGravidez.absolutePath(),
    updates: {
      [metaSoapState.preNatal.batimentoCardiacoFetal2.absolutePath()]: (
        tipoGravidez: TipoGravidezSelectFieldModel,
        allValues: SoapState
      ) => {
        if (tipoGravidez?.value === TipoGravidezEnum.UNICA) {
          return undefined
        }
        return resolveValue(allValues, metaSoapState.preNatal.batimentoCardiacoFetal2)
      },
    },
  },
  {
    field: [metaSoapState.acompanhamentoPreNatal.idadeGestacional.absolutePath()],
    updates: {
      [metaSoapState.preNatal.fonteIdadeGestacional.absolutePath()]: (
        idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal,
        allValues: SoapState
      ) => {
        const { ecograficaEmDias } = idadeGestacional ?? {}

        const fonteIg = resolveValue<FonteIdadeGestacionalEnum>(allValues, metaSoapState.preNatal.fonteIdadeGestacional)
        const formAtivo = resolveValue<FormAtivoObjetivoEnum>(
          allValues,
          metaSoapState.objetivo.atendimentosEspecificos.formAtivo
        )

        if (
          isUndefinedOrNull(ecograficaEmDias) &&
          fonteIg === FonteIdadeGestacionalEnum.US &&
          formAtivo === FormAtivoObjetivoEnum.PRE_NATAL
        ) {
          return FonteIdadeGestacionalEnum.DUM
        }

        return fonteIg
      },
    },
  },
]
